import React, { useEffect, useState } from "react";
import { Table, Button, Form, Badge, Spinner, Row, Col, Modal } from "react-bootstrap";
import api from "../../api";
import Can from "../../components/Can";
import { toast } from "react-toastify";
import "./PerformanceScorecardPage.css";
import PerformanceScoreBreakdown from "../../components/performance/PerformanceScoreBreakdown";

// Phase 5, §9/§24/§32. Scorecard list + calculate/recalculate/finalize/
// invalidate actions + transparent breakdown view. Recalculation always
// requires a reason; a finalized scorecard cannot be recalculated until
// explicitly invalidated first (both enforced server-side).
const STATUS_VARIANTS = { DRAFT: "secondary", CALCULATED: "info", FINALIZED: "success", INVALIDATED: "danger" };

export default function PerformanceScorecardPage() {
  const [scorecards, setScorecards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [recalcTarget, setRecalcTarget] = useState(null);
  const [recalcReason, setRecalcReason] = useState("");

  const [calcForm, setCalcForm] = useState({ period_id: "", user_ids: "", role_id: "", department_id: "", project_id: "", project_module_id: "" });
  const [showCalcModal, setShowCalcModal] = useState(false);
  const [calculating, setCalculating] = useState(false);

  const fetchScorecards = async () => {
    setLoading(true);
    try {
      const res = await api.get("/performance-scorecards", { params: { status: statusFilter || undefined, pageSize: 100 } });
      setScorecards(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch scorecards");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchScorecards(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const viewDetail = async (s) => {
    try {
      const res = await api.get(`/performance-scorecards/${s.id}`);
      setDetail(res.data?.data || null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load scorecard");
    }
  };

  const doFinalize = async (s) => {
    if (!window.confirm("Finalize this scorecard? It will become immutable until explicitly invalidated.")) return;
    try {
      await api.post(`/performance-scorecards/${s.id}/finalize`);
      toast.success("Scorecard finalized");
      fetchScorecards();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to finalize (missing mandatory data?)");
    }
  };

  const doInvalidate = async (s) => {
    if (!window.confirm("Invalidate this scorecard? A new calculation will be required.")) return;
    try {
      await api.post(`/performance-scorecards/${s.id}/invalidate`);
      toast.success("Scorecard invalidated");
      fetchScorecards();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to invalidate");
    }
  };

  const doRecalculate = async () => {
    if (!recalcReason.trim()) { toast.warning("A reason is required to recalculate"); return; }
    try {
      await api.post(`/performance-scorecards/${recalcTarget.id}/recalculate`, { reason: recalcReason });
      toast.success("Scorecard recalculated");
      setRecalcTarget(null);
      setRecalcReason("");
      fetchScorecards();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to recalculate");
    }
  };

  const doCalculate = async () => {
    const userIds = calcForm.user_ids.split(",").map((s) => s.trim()).filter(Boolean).map(Number);
    if (!calcForm.period_id || !userIds.length) { toast.warning("period_id and at least one user_id are required"); return; }
    setCalculating(true);
    try {
      const res = await api.post("/performance-scorecards/calculate", { period_id: calcForm.period_id, user_ids: userIds, role_id: calcForm.role_id || undefined, department_id: calcForm.department_id || undefined, project_id: calcForm.project_id || undefined, project_module_id: calcForm.project_module_id || undefined });
      toast.success(res.data?.message || "Calculation complete");
      setShowCalcModal(false);
      fetchScorecards();
    } catch (err) {
      toast.error(err.response?.data?.message || "Calculation failed");
    } finally {
      setCalculating(false);
    }
  };

  return (
    <div className="unit-page perfscorecard-theme">
      <div className="unit-header">
        <div>
          <div className="unit-page-label">Performance</div>
          <h2>Performance Scorecards</h2>
          <p>Calculated, transparent snapshots — never silently overwritten once finalized.</p>
        </div>
        <div className="unit-header-actions">
          <Form.Select
            className="perfscorecard-status-filter"
            style={{ maxWidth: 180 }}
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filter scorecards by status"
          >
            <option value="">All Statuses</option>
            {Object.keys(STATUS_VARIANTS).map((s) => <option key={s} value={s}>{s}</option>)}
          </Form.Select>
          <Can moduleKey="performance_scorecards" actionKey="calculate">
            <Button className="unit-btn unit-btn-primary" onClick={() => setShowCalcModal(true)}>
              <i className="fa-solid fa-calculator" aria-hidden="true"></i>
              Calculate
            </Button>
          </Can>
        </div>
      </div>

      <div className="unit-card">
        <div className="unit-toolbar">
          <div>
            <h5>
              <i className="fa-solid fa-chart-line perfscorecard-list-icon" aria-hidden="true"></i>
              Scorecard List
            </h5>
            <p>Per-user, per-period scores — view the breakdown, recalculate with a reason, or finalize once ready.</p>
          </div>
        </div>

        <div className="unit-table-wrapper">
          {loading ? <div className="unit-loader"><Spinner animation="border" /></div> : (
            <Table responsive className="unit-table">
              <thead><tr><th>User</th><th>Period</th><th>Profile</th><th>Total Score</th><th>Status</th><th className="text-end">Actions</th></tr></thead>
              <tbody>
                {scorecards.length ? scorecards.map((s) => (
                  <tr key={s.id}>
                    <td>{s.user_name}</td>
                    <td>{s.period_name}</td>
                    <td>{s.profile_name}</td>
                    <td>
                      {s.total_score != null ? (
                        <strong className="perfscorecard-score">{s.total_score}</strong>
                      ) : (
                        <span className="perfscorecard-muted">Not Available</span>
                      )}
                    </td>
                    <td><Badge bg={STATUS_VARIANTS[s.status] || "secondary"} className="perfscorecard-status">{s.status}</Badge></td>
                    <td>
                      <div className="unit-actions perfscorecard-actions" style={{ flexWrap: "wrap" }}>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          className="perfscorecard-action-btn"
                          onClick={() => viewDetail(s)}
                          aria-label={`View scorecard for ${s.user_name || "user"}`}
                        >
                          <i className="fa-solid fa-eye" aria-hidden="true"></i>
                          View
                        </Button>
                        {s.status !== "FINALIZED" && (
                          <Can moduleKey="performance_scorecards" actionKey="recalculate">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              className="perfscorecard-action-btn"
                              onClick={() => setRecalcTarget(s)}
                              aria-label={`Recalculate scorecard for ${s.user_name || "user"}`}
                            >
                              <i className="fa-solid fa-rotate" aria-hidden="true"></i>
                              Recalculate
                            </Button>
                          </Can>
                        )}
                        {s.status === "CALCULATED" && (
                          <Can moduleKey="performance_scorecards" actionKey="finalize">
                            <Button
                              size="sm"
                              variant="outline-success"
                              className="perfscorecard-action-btn"
                              onClick={() => doFinalize(s)}
                              aria-label={`Finalize scorecard for ${s.user_name || "user"}`}
                            >
                              <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
                              Finalize
                            </Button>
                          </Can>
                        )}
                        {s.status !== "INVALIDATED" && (
                          <Can moduleKey="performance_scorecards" actionKey="invalidate">
                            <Button
                              size="sm"
                              variant="outline-danger"
                              className="perfscorecard-action-btn"
                              onClick={() => doInvalidate(s)}
                              aria-label={`Invalidate scorecard for ${s.user_name || "user"}`}
                            >
                              <i className="fa-solid fa-ban" aria-hidden="true"></i>
                              Invalidate
                            </Button>
                          </Can>
                        )}
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6">
                      <div className="unit-empty">
                        <div className="unit-empty-icon">
                          <i className="fa-solid fa-chart-line" aria-hidden="true"></i>
                        </div>
                        <h5>No scorecards found</h5>
                        <p>Try another status filter or calculate new scorecards.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>
      </div>

      <Modal
        show={!!detail}
        onHide={() => setDetail(null)}
        centered
        size="xl"
        className="unit-modal perfscorecard-modal"
        dialogClassName="admin-teal-modal admin-teal-modal-xl"
        backdropClassName="admin-teal-backdrop"
      >
        <Modal.Header closeButton closeLabel="Close">
          <div className="d-flex align-items-center">
            <i className="fa-solid fa-chart-line modal-header-icon" aria-hidden="true"></i>
            <Modal.Title>Scorecard — {detail?.user_name} ({detail?.period_name})</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body><PerformanceScoreBreakdown scorecard={detail} /></Modal.Body>
      </Modal>

      <Modal
        show={!!recalcTarget}
        onHide={() => setRecalcTarget(null)}
        centered
        className="unit-modal perfscorecard-modal"
        dialogClassName="admin-teal-modal"
        backdropClassName="admin-teal-backdrop"
      >
        <Modal.Header closeButton closeLabel="Close">
          <div className="d-flex align-items-center">
            <i className="fa-solid fa-rotate modal-header-icon" aria-hidden="true"></i>
            <Modal.Title>Recalculate Scorecard</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form.Label>Reason (required)</Form.Label>
          <Form.Control as="textarea" rows={2} value={recalcReason} onChange={(e) => setRecalcReason(e.target.value)} />
        </Modal.Body>
        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={() => setRecalcTarget(null)}>Cancel</Button>
          <Button className="unit-btn unit-btn-primary" onClick={doRecalculate}>
            <i className="fa-solid fa-rotate" aria-hidden="true"></i>
            Recalculate
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showCalcModal}
        onHide={() => setShowCalcModal(false)}
        centered
        size="lg"
        className="unit-modal perfscorecard-modal"
        dialogClassName="admin-teal-modal admin-teal-modal-lg"
        backdropClassName="admin-teal-backdrop"
      >
        <Modal.Header closeButton closeLabel="Close">
          <div className="d-flex align-items-center">
            <i className="fa-solid fa-calculator modal-header-icon" aria-hidden="true"></i>
            <Modal.Title>Calculate Scorecards</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={4}><Form.Label>Period ID</Form.Label><Form.Control value={calcForm.period_id} onChange={(e) => setCalcForm({ ...calcForm, period_id: e.target.value })} /></Col>
            <Col md={8}><Form.Label>User IDs (comma-separated)</Form.Label><Form.Control value={calcForm.user_ids} onChange={(e) => setCalcForm({ ...calcForm, user_ids: e.target.value })} /></Col>
            <Col md={3}><Form.Label>Role ID (optional)</Form.Label><Form.Control value={calcForm.role_id} onChange={(e) => setCalcForm({ ...calcForm, role_id: e.target.value })} /></Col>
            <Col md={3}><Form.Label>Department ID (optional)</Form.Label><Form.Control value={calcForm.department_id} onChange={(e) => setCalcForm({ ...calcForm, department_id: e.target.value })} /></Col>
            <Col md={3}><Form.Label>Project ID (optional)</Form.Label><Form.Control value={calcForm.project_id} onChange={(e) => setCalcForm({ ...calcForm, project_id: e.target.value })} /></Col>
            <Col md={3}><Form.Label>Module ID (optional)</Form.Label><Form.Control value={calcForm.project_module_id} onChange={(e) => setCalcForm({ ...calcForm, project_module_id: e.target.value })} /></Col>
          </Row>
          <div className="text-muted small mt-2">The backend resolves the applicable profile per user via the standard hierarchy — it is never chosen directly here.</div>
        </Modal.Body>
        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={() => setShowCalcModal(false)}>Cancel</Button>
          <Button className="unit-btn unit-btn-primary" onClick={doCalculate} disabled={calculating}>
            {calculating ? "Calculating..." : (
              <>
                <i className="fa-solid fa-calculator" aria-hidden="true"></i>
                Calculate
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
